import React, { useEffect } from "react";
import { Helmet } from "react-helmet";
import { QuoteReferralState, QuoteState } from "kw/api/zeus";

import { AdminAdjustQuoteButton } from "./components/AdminAdjustQuoteButton";
import { AdminCoversSection } from "./components/AdminCoversSection";

import { Tab, Tabs } from "baseui/tabs-motion";

import { AdminDocumentsSection } from "./components/AdminDocumentsSection";
import { AdminEditPolicyHolderButton } from "./components/AdminEditPolicyHolderButton";
import { AdminEditQuoteDetailsButton } from "./components/AdminEditQuoteDetailsButton";
import { AdminEmailQuoteButton } from "./components/AdminEmailQuoteModal";
import { AdminExposureItemsSection } from "./components/AdminExposureItemsSection";

import { AdminHijackUserButton } from "kw/admin/components/AdminHijackUserButton";
import { AdminNotesSection } from "./components/AdminNotesSection";
import { AdminPremiumBreakdownSection } from "./components/AdminPremiumBreakdownSection";
import { AdminQuoteReferralButton } from "./components/AdminQuoteReferralModal";
import { AdminQuoteSummarySection } from "./components/AdminQuoteSummarySection";
import { AdminQuoteMessagesSection } from "kw/admin/components/AdminQuoteMessagesSection";
import { AdminRiskStateSection } from "./components/AdminRiskStateSection";
// import { ClimateModal } from "kw/util/DisplayRegistry/AddressDisplay";
import { HeadingActionsBar } from "kanopi-components/HeadingActionBar/HeadingActionsBar";
import { Button, KIND, SIZE as BUTTON_SIZE, SIZE } from "kanopi-components/Button";
import { NoteAssociationLimit } from "kw/api/zeus";
import { Placeholder } from "kanopi-components/Placeholder";
import { SummaryCard } from "./components/SummaryCard";
import { frequencyToString } from "kw/util/utils";
import { toaster } from "kanopi-components/ToastContainer";
import { useAPI } from "kw/api/useAPI";
import { useFormatter } from "kw/util/useFormatter";
import { useGlobal } from "kanopi-components/useGlobal";
import { useQuotes } from "./hooks/useQuotes";
import { useUser } from "kw/auth/useUser";
import { useStyletron } from "kanopi-components/theme/useStyletron";
import { VerticalSpacer } from "kanopi-components/SpaceWrapper/VerticalSpacer";
import { AdminExposuresPolicySection } from "./components/AdminExposuresPolicySection";
import useQuoteStatus from "./hooks/useQuoteStatus";

export const QuoteDetailsPage = ({ quoteId }: { quoteId: string }) => {
  const {
    quote,
    notes,
    loading,
    fetchQuote,
    adjustQuote: repriceQuote,
    repricing,
    loadingReprised,
  } = useQuotes();
  const user = useUser();
  const [, setHijack] = useGlobal("hijack");
  const [quoteTabActiveKey, setQuoteTabActiveKey] = React.useState("0");
  const [adjustmentsTabActiveKey, setAdjustmentsTabActiveKey] = React.useState(0);
  const [messagesTabActiveKey, setMessagesTabActiveKey] = React.useState(0);

  const headingCaptionProps = useQuoteStatus(quote);

  useEffect(() => {
    if (quoteId) {
      fetchQuote(quoteId);
    }
  }, [quoteId]);
  useEffect(() => {
    if (quote?.id) {
      const quoteSubmitted = quote?.stateDisplay === "Submitted";
      const nextUrl = quoteSubmitted ? "/policies" : "/quote/ready";
      setHijack({
        nextUrl: nextUrl,
        releaseToUrl: `/admin/quotes/${quoteId}`,
      });
      setAdjustmentsTabActiveKey(quote?.riskProfile?.riskData?.adjustments ? 0 : 1);
    }

    if (quote?.messages || quote?.privateMessages) {
      setMessagesTabActiveKey(parsedPrivateMessages ? 0 : 1);
    }
  }, [quote]);

  const [, theme] = useStyletron();

  const displayAcceptDeclineQuoteButton =
    quote?.state === QuoteState.REFERRED &&
    quote?.referralState === QuoteReferralState.PENDING;
  const displayHijackUserButton = quote?.state != QuoteState.EXPIRED;
  const { formatCurrency } = useFormatter();

  const adjustedPremium = `${formatCurrency(quote?.periodicPrice)} ${frequencyToString(
    quote?.paymentFrequency
  )}`;
  const quotedPremium = `${formatCurrency(
    quote?.unadjustedPostTaxPremium
  )} ${frequencyToString(quote?.paymentFrequency)}`;

  const handleEditSuccess = (message = "Quote has been updated.") => {
    toaster({
      kind: "POSITIVE",
      content: message,
      key: "edit-referred-quote-success-toast",
    });
    fetchQuote(quote.id);
  };

  const acceptQuoteText =
    "Are you sure you want to accept this quote? You will not be able to edit this " +
    "quote in the future.";
  const declineQuoteText =
    "Are you sure you want to decline this quote? You will not be able to edit this " +
    "quote in the future. However, this quote will be accessible for reporting and " +
    "viewing only purposes.";

  const { mutation } = useAPI();
  const updateQuoteState = (
    newState: QuoteReferralState.ACCEPTED | QuoteReferralState.DECLINED,
    note: string
  ) => {
    mutation(
      {
        admin: {
          updateQuoteReferralState: [
            {
              referralState: newState,
              quoteId: quote.id,
              note,
            },
            {
              success: true,
              note: {
                id: true,
                content: true,
              },
            },
          ],
        },
      },
      (response: { admin: { updateQuoteReferralState: { success: boolean } } }) => {
        // An unsuccessful response will generate an error handled by the API hook
        if (response.admin.updateQuoteReferralState.success) {
          toaster({
            kind: "POSITIVE",
            content: `Quote has been ${newState.toLocaleLowerCase()}.`,
            key: "decline-referred-quote-success-toast",
          });
          fetchQuote(quote.id);
        }
      }
    );
  };

  const ctaButtons = [
    displayAcceptDeclineQuoteButton && (
      <Button
        key={10}
        outlined={true}
        size={SIZE.compact}
        onClick={() => {
          repriceQuote(quote);
        }}
      >
        Reprice
      </Button>
    ),
    displayAcceptDeclineQuoteButton && (
      <AdminQuoteReferralButton
        key={0}
        quote={quote}
        fetchQuote={fetchQuote}
        color={theme.colors.positive}
        title="Accept"
        modalText={acceptQuoteText}
        modalButtonLabel="Yes, accept this quote"
        newState={QuoteReferralState.ACCEPTED}
        updateQuoteState={updateQuoteState}
        data-testid={"acceptButton"}
      />
    ),
    displayAcceptDeclineQuoteButton && (
      <AdminQuoteReferralButton
        key={1}
        quote={quote}
        fetchQuote={fetchQuote}
        size={BUTTON_SIZE.compact}
        color={theme.colors.negative}
        title="Decline"
        modalText={declineQuoteText}
        modalButtonLabel="Yes, decline this quote"
        newState={QuoteReferralState.DECLINED}
        updateQuoteState={updateQuoteState}
        data-testid={"declineButton"}
      />
    ),
    displayAcceptDeclineQuoteButton && (
      <AdminAdjustQuoteButton
        key={2}
        onSuccess={() => handleEditSuccess("Quote has been adjusted.")}
        buttonTitle="Adjust"
        kind={KIND.primary}
        quote={quote}
        size={BUTTON_SIZE.compact}
        data-testid={"adjustButton"}
        modalTitle="Quote adjust"
      />
    ),
    quote?.state === QuoteState.QUOTED && (
      <AdminEmailQuoteButton key={3} quote={quote} size={BUTTON_SIZE.compact} />
    ),
    displayHijackUserButton && (
      <AdminHijackUserButton
        key={4}
        userId={quote?.riskProfile?.owner?.id}
        userIsActive={quote?.riskProfile?.owner?.isActive}
      />
    ),
  ];

  const hasEnabledExposures = !!quote?.riskProfile?.enabledExposures?.length;

  const parsedPrivateMessages =
    quote?.privateMessages && JSON.parse(quote?.privateMessages);
  const parsedMessages = quote?.messages && JSON.parse(quote?.messages);

  // TODO fix all the silly nesting and optional checks, its hard to read
  return (
    <>
      <Helmet>
        <title>{`Quote ${quote?.id} - ${user?.organization?.name}`}</title>
      </Helmet>
      <Placeholder loading={!quote || loading}>
        <VerticalSpacer>
          {/* <ClimateModal
            data={climateData?.elements}
            isOpen={climateModalIsOpen}
            setIsOpen={setClimateModalIsOpen}
          /> */}
          <HeadingActionsBar
            heading={`Quote #${quote?.id}`}
            actions={ctaButtons}
            {...headingCaptionProps}
          />
          <Placeholder loading={repricing || loadingReprised}>
            <VerticalSpacer>
              {(parsedMessages || parsedPrivateMessages) && (
                <SummaryCard data-testid="adminQuoteMessagesSection" title="Messages">
                  <Tabs
                    activeKey={messagesTabActiveKey}
                    onChange={({ activeKey }) => {
                      setMessagesTabActiveKey(activeKey);
                    }}
                    activateOnFocus
                  >
                    {parsedPrivateMessages && (
                      <Tab
                        title={parsedPrivateMessages.title}
                        data-testid="quoteReferralMessagesSection"
                      >
                        <AdminQuoteMessagesSection
                          messages={parsedPrivateMessages.value}
                        />
                      </Tab>
                    )}
                    {parsedMessages && (
                      <Tab
                        title={parsedMessages.title}
                        data-testid="quoteDeclinedMessagesSection"
                      >
                        <AdminQuoteMessagesSection messages={parsedMessages.value} />
                      </Tab>
                    )}
                  </Tabs>
                </SummaryCard>
              )}

              <SummaryCard showHeader={false}>
                <Tabs
                  activeKey={quoteTabActiveKey}
                  onChange={({ activeKey }) => {
                    setQuoteTabActiveKey(activeKey);
                  }}
                  activateOnFocus
                >
                  <Tab title="Summary" data-testid="quoteSummarySection">
                    <AdminQuoteSummarySection quote={quote} />
                  </Tab>
                  <Tab
                    title="Premium breakdown"
                    data-testid="quotePremiumBreakdownSection"
                  >
                    <AdminPremiumBreakdownSection quote={quote} />
                  </Tab>
                  <Tab title="Documents" data-testid="quoteDocumentsSection">
                    <AdminDocumentsSection
                      documents={
                        quote?.policyPeriod
                          ? quote.policyPeriod.documents
                          : quote?.documents
                      }
                    />
                  </Tab>
                </Tabs>
              </SummaryCard>

              {quote?.riskProfile?.knockoutQuestionSets.length > 0 ? (
                <SummaryCard
                  data-testid="adminKnockoutSection"
                  title="Referral questions"
                >
                  <AdminRiskStateSection
                    riskProfile={quote?.riskProfile}
                    questionSets={quote?.riskProfile?.knockoutQuestionSets}
                    referredQuestionSetKeys={quote?.referredQuestionSetKeys}
                    highlightReferred={quote?.state === QuoteState.REFERRED}
                    hasLongLabels={true}
                  />
                </SummaryCard>
              ) : null}

              {/*
              TODO: DEV-4772 remove else part once products migrated to have exposures
              */}

              {hasEnabledExposures ? (
                <>
                  <SummaryCard data-testid="adminExposuresPolicySection" title="Policy">
                    <AdminExposuresPolicySection
                      enabledExposures={quote?.riskProfile?.enabledExposures.filter(
                        (e) => {
                          return e.type === "policy";
                        }
                      )}
                      riskProfile={quote?.riskProfile}
                      availableCovers={quote?.product?.availableCovers}
                      quote={quote}
                    />
                  </SummaryCard>
                  <SummaryCard data-testid="adminExposuresSection" title="Exposures">
                    <AdminExposureItemsSection
                      enabledExposures={quote?.riskProfile?.enabledExposures.filter(
                        (e) => {
                          return e.type !== "policy";
                        }
                      )}
                      availableCovers={quote?.product?.availableCovers}
                      quote={quote}
                      riskProfile={quote?.riskProfile}
                    />
                  </SummaryCard>
                </>
              ) : (
                <SummaryCard data-testid="adminCoversSection" title="Covers">
                  <AdminCoversSection
                    covers={quote?.covers?.map((cover) => cover?.key)}
                    availableCovers={quote?.product?.availableCovers}
                  />
                </SummaryCard>
              )}
              {quote?.riskProfile?.policyHolderQuestionSets.length > 0 ? (
                <SummaryCard
                  data-testid="adminPolicyHolderSection"
                  title="Policy holder questions"
                  ctas={[
                    ((quote?.state === QuoteState.REFERRED &&
                      quote?.referralState === QuoteReferralState.PENDING) ||
                      (quote?.state === QuoteState.QUOTED &&
                        quote?.referralState === QuoteReferralState.ACCEPTED)) && (
                      <AdminEditPolicyHolderButton
                        key={0}
                        onSuccess={handleEditSuccess}
                        quote={quote}
                        size={BUTTON_SIZE.mini}
                        modalTitle="Policy holder questions"
                      />
                    ),
                  ]}
                >
                  <AdminRiskStateSection
                    riskProfile={quote?.riskProfile}
                    questionSets={quote?.riskProfile?.policyHolderQuestionSets}
                  />
                </SummaryCard>
              ) : null}
              <SummaryCard
                data-testid="adminRiskStateSection"
                title="Quote details"
                ctas={[
                  quote?.state === QuoteState.REFERRED &&
                    quote?.referralState === QuoteReferralState.PENDING && (
                      <AdminEditQuoteDetailsButton
                        key={0}
                        onSuccess={handleEditSuccess}
                        quote={quote}
                        size={BUTTON_SIZE.mini}
                        modalTitle="Quote questions"
                      />
                    ),
                ]}
              >
                <AdminRiskStateSection
                  riskProfile={quote?.riskProfile}
                  questionSets={quote?.riskProfile?.riskStateQuestionSets}
                  referredQuestionSetKeys={quote?.referredQuestionSetKeys}
                  highlightReferred={quote?.state === QuoteState.REFERRED}
                  hasLongLabels={true}
                />
              </SummaryCard>

              <SummaryCard showHeader={false}>
                <Tabs
                  activeKey={adjustmentsTabActiveKey}
                  onChange={({ activeKey }) => {
                    setAdjustmentsTabActiveKey(activeKey);
                  }}
                  activateOnFocus
                >
                  {quote?.riskProfile?.riskData?.adjustments && (
                    <Tab title="Adjustments" data-testid="quoteAdjustmentSection">
                      <AdminRiskStateSection
                        riskProfile={quote?.riskProfile}
                        questionSets={quote?.riskProfile?.adjustmentQuestionSets}
                        additionalData={[
                          {
                            id: "quoted_premium",
                            question: { label: "Quoted premium", inputType: "string" },
                            answer: quotedPremium,
                          },
                          {
                            id: "adjusted_premium",
                            question: {
                              label: "Adjusted premium",
                              inputType: "string",
                            },
                            answer: adjustedPremium,
                          },
                        ]}
                      />
                    </Tab>
                  )}
                  <Tab title="Notes" data-testid="quoteNotesSection">
                    <AdminNotesSection
                      notes={notes}
                      fetchAssocObj={fetchQuote}
                      assocObjId={quote?.id}
                      assocObjModel={NoteAssociationLimit.QUOTE}
                    />
                  </Tab>
                </Tabs>
              </SummaryCard>
            </VerticalSpacer>
          </Placeholder>
        </VerticalSpacer>
      </Placeholder>
    </>
  );
};
